import { type FC, useCallback, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Form, type InjectedFormProps, reduxForm } from "redux-form";

import { ContentPlaceholder } from "@/ui-modules/core/ContentPlaceholder.tsx";

import { useGetFormError } from "../../../app-utils/hooks/useForm.js";
import { type Dictionary } from "../../../app-utils/type.js";
import {
  NfParamTypeEnum,
  type NfParamValue,
  type NfPlaybookParam,
  type Organism,
  type Playbook,
} from "../../../graphql.js";
import { updateNfPlaybookExecutionState } from "../../../redux/modules/nf-playbook-execution.js";
import { type IAppState } from "../../../redux/reducer.js";
import { type ReduxDispatch } from "../../../redux/redux-utils.js";
import { NF_PLAYBOOK_SCREEN_IDS, PLAYBOOK_TYPE } from "../../../settings/settings.js";
import PlaybookWizardStep, {
  type IPlaybookWizardStepInjectedProps,
  type IPlaybookWizardStepProvidedProps,
} from "../PlaybookWizardStep.js";
import NfWorkflowParamInputField, { isHiddenNfParamType } from "./NfWorkflowParamInputField.js";

type INfPlaybookWorkflowParamsScreenProps = IPlaybookWizardStepProvidedProps &
  InjectedFormProps<FormData, any> &
  IPlaybookWizardStepInjectedProps;

const NfPlaybookWorkflowParamsScreen: FC<INfPlaybookWorkflowParamsScreenProps> = (props) => {
  const dispatch = useDispatch<ReduxDispatch>();
  const globalFormError = useGetFormError(NF_PLAYBOOK_SCREEN_IDS.NF_WORKFLOW_PARAMS_SCREEN);
  const playbook = useSelector<IAppState, Playbook>((state) => state.nfPlaybookExecution.playbook);
  const workflowValues = useSelector<IAppState, Dictionary<NfParamValue[]>>(
    (state) => state.nfPlaybookExecution.workflowValues,
  );
  const selectedGenome = useSelectedExplorerGenome(NF_PLAYBOOK_SCREEN_IDS.NF_WORKFLOW_PARAMS_SCREEN);

  useEffect(() => {
    if (!props.dirty) {
      // for some reason the initialize() call when going back and forth between screens (while keeping form data alive using destroyOnUnmount) does not do the right thing, reseting redux form data to null !
      props.initialize(workflowValues);
    }
  }, []);

  const handleSubmitData = useCallback((values) => {
    dispatch(
      updateNfPlaybookExecutionState({
        workflowValues: { ...workflowValues, ...values },
      }),
    );
    props.proceedWithNextStep();
  }, []);

  const workflowParameters = playbook.nfConfig.workflowParameters
    .filter((workflowParam) => !workflowParam.hidden && !isHiddenNfParamType(workflowParam.type))
    .filter((workflowParam) => {
      if (playbook.type.id === PLAYBOOK_TYPE.NEXTFLOW_ANALYSIS_WITH_REGISTERED_SAMPLES) {
        // in case of Tracker integration, hide the input field corresponding to the raw data file input, since this input is mapped from the sample grid
        // Previous implementation by Seb was: return !isRawDataFileTypeNfParam(workflowParam);
        // This seems much more basic than what Devon suggested in SEL11-1510, but the suggestion seems reasonable:
        return !playbook.nfConfig?.trackerParameters?.sampleAttributes
          ?.filter((a) => !!a?.workflowParam) // check which attributes have workflow param association
          .map((a) => a?.workflowParam) // collect the ID of these params
          .includes(workflowParam?.identifier); //check if the param is included in the list - if so, filter it out
      }
      return true;
    });

  return (
    <PlaybookWizardStep
      {...props}
      playbookPage={{
        ...props.playbookPage,
      }}
      wizardFooterData={{
        error: globalFormError || props.error,
        nextButtonProps: {
          label: "Review",
          isLoading: props.submitting,
          onPress: props.handleSubmit(handleSubmitData),
        },
      }}
    >
      {workflowParameters.length ? (
        <Form className="form-horizontal">
          {props.initialized &&
            workflowParameters.map((workflowParam) => (
              <NfWorkflowParamInputField
                key={workflowParam.identifier}
                workflowParam={workflowParam}
                selectedGenome={selectedGenome}
                horizontalLayout={true}
              />
            ))}
        </Form>
      ) : (
        <ContentPlaceholder>This Playbook does not require any input, you can proceed.</ContentPlaceholder>
      )}
    </PlaybookWizardStep>
  );
};

export default reduxForm<any, IPlaybookWizardStepProvidedProps>({
  form: NF_PLAYBOOK_SCREEN_IDS.NF_WORKFLOW_PARAMS_SCREEN,
  destroyOnUnmount: false,
})(NfPlaybookWorkflowParamsScreen);

export function useSelectedExplorerGenome(formName: string): Organism {
  const workflowParams = useSelector<IAppState, NfPlaybookParam[]>(
    (state) => state.nfPlaybookExecution.playbook.nfConfig.workflowParameters,
  );
  const formState = useSelector<IAppState, any>((state) => state.form[formName]?.values);

  const selectedGenome: Organism = useMemo(() => {
    const genomeWorkflowParam = workflowParams.find(
      (workflowParam) => workflowParam.type.type === NfParamTypeEnum.Genome,
    );
    const nfParamValues: NfParamValue[] = formState?.[genomeWorkflowParam?.identifier];
    //  first verify that an object (= Explorer Genome) is selected, and not an array (files or folder)
    return nfParamValues?.find((nfParamValue) => nfParamValue.organism)?.organism;
  }, [workflowParams, formState]);

  return selectedGenome;
}
